import AccessTime from '@material-ui/icons/AccessTime';
import {
  KeyboardTimePicker,
  KeyboardTimePickerProps,
} from '@material-ui/pickers';
import { format, isValid, parse } from 'date-fns';
import { NullableString } from 'mapistry-shared';
import React, { useCallback, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { ConditionalWrapper } from '../ConditionalWrapper';
import { VisuallyHidden } from '../VisuallyHidden';
import {
  getCommonPickerProps,
  muiPickerCss,
  MuiPickerPropsToOmitOnWrapper,
  PickerInputLabel,
  PickerSubText,
  useOnChangeWrapper,
} from './shared';

type TimePickerProps = Omit<
  KeyboardTimePickerProps,
  MuiPickerPropsToOmitOnWrapper | 'ampm' | 'value'
> & {
  errorText?: string;
  subText?: NullableString;
  // this matches the signature for html input & final form field's onChange
  onChange: (event: { target: { value?: string | null } }) => void;
  name: string;
  // value as a string better represents time than a Date object
  value?: string | null;
  dataFormat?: string;
  ampm?: boolean;
  isLabelHidden?: boolean;
  isErrorTextHidden?: boolean;
  isCompact?: boolean;
  isSubtextHidden?: boolean;
  dontSaveSpaceForErrorText?: boolean;
};

const StyledTimePicker = styled(KeyboardTimePicker)<{
  $isCompact?: boolean;
}>`
  ${muiPickerCss}
  ${({ $isCompact }) =>
    $isCompact &&
    css`
      & .MuiFormControlLabel-root {
        margin-right: 0;
      }

      & .MuiInputBase-root {
        min-width: 5rem;
        padding: 0 0 0 0.5rem;
      }

      & .MuiInputAdornment-positionEnd {
        margin-right: -8px;
        margin-left: -8px;
      }
    `};
`;

export function TimePicker({
  value,
  onChange: onChangeProp,
  label,
  subText,
  error,
  errorText,
  name,
  required,
  dataFormat = 'HH:mm',
  ampm = false,
  isLabelHidden,
  isErrorTextHidden,
  isCompact,
  isSubtextHidden,
  dontSaveSpaceForErrorText = false,
  ...rest
}: TimePickerProps) {
  const [idRandomify] = useState(uuidv4);
  const pickerInputElId = `${name}-id-${idRandomify}`;
  const valueAsDate = useMemo(
    () => (value ? parse(value, dataFormat, new Date()) : value),
    [value, dataFormat],
  );
  const onChange = useOnChangeWrapper(
    onChangeProp,
    // the MUI picker returns a Date object for time, with the selected time on today's date
    // If valid, formats it as a string in the data format (default HH:mm)
    // Otherwise, just returns the entered string (which can be checked for validation)
    (date, textValue) =>
      date && isValid(date) ? format(date, dataFormat) : textValue,
  );

  // When in AM/PM mode, accept and convert valid military time entries on blur
  const convertMilitaryTimeToAmpm = useCallback(() => {
    if (ampm && value && !isValid(valueAsDate)) {
      const militaryTimeDate = parse(
        value.replace('_', '').trim(),
        'HH:mm',
        new Date(),
      );

      if (isValid(militaryTimeDate)) {
        onChange(militaryTimeDate, value);
      }
    }
  }, [ampm, onChange, value, valueAsDate]);

  return (
    <div>
      <ConditionalWrapper
        condition={!!isLabelHidden}
        /* eslint-disable-next-line react/no-unstable-nested-components */
        wrapper={(children) => <VisuallyHidden>{children}</VisuallyHidden>}
      >
        <PickerInputLabel
          required={required}
          error={error}
          htmlFor={pickerInputElId}
          label={label}
        />
      </ConditionalWrapper>
      <StyledTimePicker
        required={required}
        error={error}
        onChange={onChange}
        value={valueAsDate || null} // an unselected date should have value === null
        ampm={ampm}
        keyboardIcon={<AccessTime />}
        $isCompact={isCompact}
        {...getCommonPickerProps(pickerInputElId)}
        inputProps={{ onBlur: convertMilitaryTimeToAmpm, ...rest.inputProps }}
        {...rest}
      />
      {(!dontSaveSpaceForErrorText || !!errorText || !!subText) && (
        <ConditionalWrapper
          condition={!!isErrorTextHidden}
          /* eslint-disable-next-line react/no-unstable-nested-components */
          wrapper={(children) => <VisuallyHidden>{children}</VisuallyHidden>}
        >
          <PickerSubText
            error={error}
            errorText={errorText}
            subText={subText}
            isSubtextHidden={isSubtextHidden}
          />
        </ConditionalWrapper>
      )}
    </div>
  );
}
